import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Calendar, Settings as SettingsIcon, FileText, ChevronLeft, LayoutGrid as ListsIcon } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { Toaster } from "@/components/ui/sonner";
import { AccountGate } from "@/components/foundations/core/AccountGate/AccountGate";
import { useTaskAlerts } from "@/hooks/useTaskAlerts";
import { SyncNotificationBadge } from "@/components/navigation/SyncNotificationBadge/SyncNotificationBadge";

type Props = {
  children: ReactNode;
  /** Optional back link; when set, header replaces the home button with a back button to that path. */
  backTo?: string;
  /** Optional custom back behavior for pages with multiple entry points. */
  onBack?: () => void;
  /** Optional page title rendered in header */
  title?: string;
  /** Hide the gear & report icons (e.g. on Settings/Reports themselves we still show them, just not back) */
  hideQuickActions?: boolean;
  /** Right-side custom slot */
  rightSlot?: ReactNode;
};

/**
 * App shell.
 *
 * The top bar is **fixed** (not just sticky) so iOS PWA installs can rely on
 * a stable header that lives behind the Dynamic Island / status bar without
 * the page scrolling beneath it. Safe-area insets are baked into both the
 * top bar and the bottom safe spacer so floating action buttons and the
 * Detail page's bottom buttons clear the home indicator.
 *
 * Icons are intentionally larger here (5×5 = ~20px) with 44×44 tap targets
 * to match iOS HIG.
 */
export default function AppShell({ children, backTo, onBack, title, hideQuickActions, rightSlot }: Props) {
  const { user, loading } = useAuth();
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Track scroll so the fixed top bar gets a stronger hairline + blur once
  // content slides under it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Run alerts polling regardless of which page the user is on.
  useTaskAlerts();

  if (loading) {
    return (
      <div className="app-shell">
        <div className="loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <AccountGate />;
  }

  const toastOffset = title
    ? "calc(env(safe-area-inset-top) + 5.75rem)"
    : "calc(env(safe-area-inset-top) + 4.25rem)";

  return (
    <div className="app-shell">
      <Toaster offset={toastOffset} />
      {/* Fixed top bar.
          - pt-safe: env(safe-area-inset-top) handled via pt-safe utility (defined in styles/index.scss).
          - backdrop-blur: iOS-native feel.
          - 56px content height keeps icon row tappable; combined with safe-area pad it grows naturally on notched devices. */}
      <header
        className={`app-header${scrolled ? " scrolled" : ""}`}
      >
        <div className="header-container">
          <div className="header-primary">
            {backTo || onBack ? (
              onBack ? (
                <button
                  className="back-button"
                  aria-label="Back"
                  onClick={onBack}
                >
                  <ChevronLeft className="back-icon" />
                </button>
              ) : (
                <Link href={backTo!}>
                  <button
                    className="back-button"
                    aria-label="Back"
                  >
                    <ChevronLeft className="back-icon" />
                  </button>
                </Link>
              )
            ) : (
              <Link href="/">
                <span className="brand">Daily Groove</span>
              </Link>
            )}
            {title && (
              <span className="page-title">
                <span className="separator">/</span>
                {title}
              </span>
            )}
          </div>

          <div className="header-nav">
            {rightSlot}
            {!hideQuickActions && (
              <>
                {/* <Link href="/">
                  <IconButton ariaLabel="Home" active={location === "/"}>
                    <DailyGrooveIcon className="h-7 w-7" />
                  </IconButton>
                </Link> */}
                <Link href="/calendar">
                  <IconButton ariaLabel="Calendar" active={location.startsWith("/calendar")}>
                    <Calendar className="nav-icon" />
                  </IconButton>
                </Link>
                <Link href="/lists">
                  <IconButton
                    ariaLabel="Lists"
                    active={location.startsWith("/lists") || location.startsWith("/lists") || location.startsWith("/list/")}
                  >
                    <ListsIcon className="nav-icon" />
                  </IconButton>
                </Link>
                <Link href="/reports">
                  <IconButton ariaLabel="Reports" active={location.startsWith("/reports")}>
                    <FileText className="nav-icon" />
                  </IconButton>
                </Link>
                <Link href="/settings">
                  <SyncNotificationBadge>
                    <IconButton ariaLabel="Settings" active={location.startsWith("/settings")}>
                      <SettingsIcon className="nav-icon" />
                    </IconButton>
                  </SyncNotificationBadge>
                </Link>
              </>
            )}
          </div>
        </div>
        {title && (
          <div className="mobile-title">
            <span>{title}</span>
          </div>
        )}
      </header>

      {/* Spacer to keep page content below the fixed bar.
          Includes safe-area top so notched devices don't hide content under the island. */}
      <div className={`header-spacer${title ? " with-title" : ""}`} aria-hidden="true" />

      <main className="app-main">{children}</main>
    </div>
  );
}

function DailyGrooveIcon({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block bg-current ${className}`}
      style={{
        WebkitMask: "url('/daily-groove.svg') center / contain no-repeat",
        mask: "url('/daily-groove.svg') center / contain no-repeat",
      }}
    />
  );
}

function IconButton({
  children,
  ariaLabel,
  onClick,
  active,
}: {
  children: ReactNode;
  ariaLabel: string;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <button
      aria-label={ariaLabel}
      onClick={onClick}
      className={`nav-button${active ? " active" : ""}`}
    >
      {children}
    </button>
  );
}
